import React, { Fragment, useRef, useState } from 'react';
import { PiDotsThreeOutlineVerticalFill } from 'react-icons/pi';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import toast from 'react-hot-toast';

import CreateTriggerBtn from './CreateTriggerBtn';
import BotStepsModal from './BotStepsModal';
import PreviousExecutionsModal from './PreviousExecutions';
import BotStepsEditModal from './BotStepsEditModal';
import TriggerForm from './TriggerForm';
import { DataState } from './../../context/DataProvider';
import TriggerDetails from './TriggerDetails';
import Modal from '../Modal';
import ExecutionComponent from './ExecutionComponent';
import { deleteBot } from '../../services/api';
import { appImages } from '../../utils/constants';
import BotToggleButton from './BotToggleButton';
import useClickOutside from '../../hooks/useClickOutside';
// appImages = {appname: imageurl}

const Trigger = () => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isBotStepsModalOpen, setBotStepsModalOpen] = useState(false);
  const [IsBotStepsEditModalOpen, setIsBotStepsEditModalOpen] = useState(null);
  const [isPreviousExecutionsModalOpen, setPreviousExecutionsModalOpen] =
    useState(false);
  const [selectedBotId, setSelectedBotId] = useState(null);
  const { bots, setBots, userGoogleId, setNeedBotFetch } = DataState();
  const walletAddressRef = useRef(null);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const openBotStepsModal = (botId) => {
    setSelectedBotId(botId);
    setBotStepsModalOpen(botId);
  };
  const closeBotStepsModal = () => {
    setBotStepsModalOpen(null);
    setSelectedBotId(null);
  };

  const openBotStepsEditModal = (botId) => {
    setSelectedBotId(botId);
    setIsBotStepsEditModalOpen(botId);
  };
  const closeBotStepsEditModal = () => {
    // setBotStepsModalOpen(null);
    setIsBotStepsEditModalOpen(null);
    setSelectedBotId(null);
  };

  const openPreviousExecutionsModal = (botId) => {
    setSelectedBotId(botId);
    setPreviousExecutionsModalOpen(botId);
  };

  const closePreviousStepsModal = () => {
    setPreviousExecutionsModalOpen(null);
    setSelectedBotId(null);
  };

  const [showDetails, setShowDetails] = useState('');
  const openShowDetails = (botId) => {
    setShowDetails(botId);
  };

  const toggleDropdown = () => {};

  useClickOutside([walletAddressRef], () => toggleDropdown(null));

  const [copyWorkflow, setCopyWorkflow] = useState(false);
  const [copyWorkflowData, setCopyWorkflowData] = useState(false);

  const handleCopyWorkflow = (item) => {
    setCopyWorkflow(true);
    setCopyWorkflowData(item);
    openModal();
  };

  const handleDeleteBot = async (botId) => {
    try {
      await deleteBot(botId, userGoogleId);
      toast.success('Deleted Succesfully');
    } catch (error) {
      toast.error(error);
    } finally {
      setNeedBotFetch(true);
    }
  };

  // This function will be called by the toggle button to update the UI
  const handleBotUpdate = (botId, newDisabledState) => {
    setBots((prevBots) =>
      prevBots.map((bot) =>
        bot.id === botId ? { ...bot, disabled: newDisabledState } : bot,
      ),
    );
  };

  return (
    <div className="w-full">
      <div className="hide-sidebar mt-5 flex max-h-[calc(100vh-200px)] flex-col gap-5 overflow-y-auto">
        {/* Set a max height for scrolling */}
        {bots.length > 0 ? (
          bots.map((item) => (
            <div
              key={item.id}
              className="br-2 bg-2 text-p-text relative flex flex-col gap-2 rounded-md border px-4 py-3 transition-colors duration-150 ease-in-out"
            >
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  {/* MODIFICATION START: Dynamically render trigger icons */}
                  {Array.isArray(item.trigger) && item.trigger.length > 0 ? (
                    item.trigger.map((triggerNode) => {
                      const triggerKey = triggerNode.data?.triggerOption;
                      // Use appImages to find the correct icon, with a fallback
                      const imageUrl =
                        appImages[triggerKey] || '/assets/logo.png';

                      return (
                        <div
                          key={triggerNode.id} // Use the unique node ID for the key
                          className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded p-0.5"
                          title={triggerNode.data?.label || triggerKey} // Add a helpful tooltip
                        >
                          <img
                            src={imageUrl}
                            alt={`${triggerKey || 'trigger'} icon`}
                          />
                        </div>
                      );
                    })
                  ) : (
                    // Fallback for bots with no triggers defined
                    <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded p-0.5">
                      <img src={'/assets/logo.png'} alt="Trigger icon" />
                    </div>
                  )}
                  {/* MODIFICATION END */}
                </div>
                <strong className="text-md flex-1 truncate font-medium">
                  {item.name}
                </strong>
                <div className="relative z-10 mb-3 flex items-center gap-4">
                  {/* MODIFICATION: Replace placeholder with the new BotToggleButton */}
                  <BotToggleButton bot={item} onUpdate={handleBotUpdate} />
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <button
                        type="button"
                        onClick={() => toggleDropdown(item.id)}
                        className="text-md text-p-text -mr-1 flex items-center rounded-[3px] border-0 bg-transparent px-1 py-1 font-semibold transition-colors duration-150 ease-in-out disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Open workflow settings</span>
                        <PiDotsThreeOutlineVerticalFill className="text-xl" />
                      </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content
                        className="DropdownMenuContent z-20 overflow-hidden rounded-lg border border-zinc-700 bg-zinc-900 p-2 text-zinc-200 shadow-lg max-sm:fixed max-sm:right-0"
                        sideOffset={10}
                      >
                        {[
                          {
                            label: 'View Details',
                            onClick: () => openShowDetails(item.id),
                          },
                          {
                            label: 'Advanced Test',
                            onClick: () => openBotStepsModal(item.id),
                          },
                          {
                            label: 'Previous Executions',
                            onClick: () => {
                              openPreviousExecutionsModal(item.id);
                            },
                          },
                          {
                            label: 'Edit',
                            onClick: () => openBotStepsEditModal(item.id),
                            hideOnMobile: true,
                          },
                          {
                            label: 'Duplicate Workflow',
                            onClick: () => handleCopyWorkflow(item),
                            hideOnMobile: true,
                          },
                          { label: 'Share' },
                          {
                            label: 'Delete',
                            onClick: () => handleDeleteBot(item.id),
                          },
                        ].map((dropdownItem, index) => {
                          const isHiddenOnMobile = dropdownItem.hideOnMobile;

                          return (
                            <DropdownMenu.Item
                              key={index}
                              className={`DropdownMenuItem ${isHiddenOnMobile ? 'hidden sm:block' : ''}`}
                            >
                              <button
                                onClick={dropdownItem.onClick}
                                className="bg-1-hover block w-full rounded-md px-5 py-2 text-left text-sm transition-all duration-150 hover:bg-zinc-800 focus:bg-gray-700 max-sm:text-xs"
                              >
                                {dropdownItem.label}
                              </button>
                            </DropdownMenu.Item>
                          );
                        })}
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                </div>
              </div>
              {isBotStepsModalOpen === item.id && (
                <BotStepsModal
                  botId={selectedBotId}
                  onClose={closeBotStepsModal}
                />
              )}
              {IsBotStepsEditModalOpen === item.id && (
                <BotStepsEditModal
                  botId={selectedBotId}
                  onClose={closeBotStepsEditModal}
                />
              )}
              {isPreviousExecutionsModalOpen === item.id && (
                <PreviousExecutionsModal
                  botId={selectedBotId}
                  onClose={closePreviousStepsModal}
                />
              )}

              <Modal
                isOpen={showDetails === item.id}
                title="Details"
                onClose={() => setShowDetails(null)}
              >
                <TriggerDetails item={item} />
              </Modal>
              <div className="flex flex-row flex-wrap gap-4">
                {item.apps_expanded.map((i, index) => (
                  <div
                    key={index}
                    className="flex flex-col gap-3 rounded-lg bg-zinc-900 px-4 py-3"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        key={i.image}
                        className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border border-zinc-300 bg-zinc-100 p-0.5"
                      >
                        <img src={i.image} alt="Trigger icon" />
                      </div>
                      <span>{i.label}</span>
                    </div>

                    {i.triggerPhrase && (
                      <div className="flex w-fit items-center rounded-[4px] border border-zinc-700 bg-zinc-800 px-2 py-1 text-sm">
                        <span className="font-semibold">
                          Specific Phrase or Keyword
                        </span>
                        : "{i.triggerPhrase}"
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {item.task_description && (
                <p className="text-s-text br-1 bg-1 mt-2 rounded-xl border px-3 py-2 text-sm font-normal">
                  <DescriptionToggle item={item} />
                </p>
              )}
              <ExecutionComponent botId={item.id} />
            </div>
          ))
        ) : (
          <div className="mx-auto flex h-96 w-full max-w-2xl flex-col items-center justify-center gap-5 rounded-lg border border-zinc-700 bg-zinc-900 p-4 text-center">
            <span className="text-sm text-zinc-400 sm:text-base">
              No workflows created yet
            </span>
            <span className="text-lg font-semibold text-zinc-100 sm:text-xl">
              Start creating with your first workflow
            </span>
            <CreateTriggerBtn onClick={openModal} />
          </div>
        )}
      </div>

      <TriggerForm
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        copyWorkflow={copyWorkflow}
        copyWorkflowData={copyWorkflowData}
      />
    </div>
  );
};

export default Trigger;

export const triggerDatabase = [
  {
    id: 1,
    name: 'When I message the assistant',
    image: 'https://oqlous.inqbator.in/assets/oqlous.svg',
  },
  {
    id: 2,
    name: 'Gmail',
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228561/mentorEducationImages/wbenqxucyj7mh0vsec5f.png',
  },
];

const DescriptionToggle = ({ item }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Function to toggle the expanded state
  const toggleDescription = () => {
    setIsExpanded(!isExpanded);
  };

  // Truncate the description to 200 characters
  const truncatedDescription =
    item.task_description.length > 400
      ? item.task_description.substring(0, 400) + '...'
      : item.task_description;

  return (
    <>
      Workflow:
      {isExpanded ? item.task_description : truncatedDescription}
      {item.task_description.length > 400 && (
        <button
          onClick={toggleDescription}
          className="ml-2 text-xs text-zinc-400 hover:underline focus:outline-none"
        >
          {isExpanded ? 'Show Less' : 'Show More'}
        </button>
      )}
    </>
  );
};
